<script setup lang="ts">
import type {
  RoamingCountryStruct,
  RoamingZoneStruct,
} from "@/struct/RoaminStruct";
import type { PropType } from "@vue/runtime-core";
import RoamingCountersListWindow from "@/components/roaming/RoamingCountersListWindow.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseModal from "@/components/base/BaseModal.vue";
import { useI18n } from "vue-i18n";
import { useRoamingInfoDetails } from "@/composables/roaming/useRoamingInfoDetails";

const props = defineProps({
  country: {
    type: Object as PropType<RoamingCountryStruct>,
    required: true,
  },
  zone: {
    type: Object as PropType<RoamingZoneStruct | null>,
    default: null,
  },
  othersCountriesList: {
    type: Object as PropType<RoamingCountryStruct[] | null>,
    default: null,
  },
  othersCountriesCount: {
    type: Number,
    default: 0,
  },
});

defineEmits(["select"]);

const { t } = useI18n();
const {
  activate,
  closeAllModal,
  conditions,
  formatAmount,
  isAlreadyAdded,
  isIncompatible,
  isLoadingActivate,
  isShowOthers,
  isSuccessful,
  isUnhandledError,
  isUnsuccessful,
  othersCountriesCountFormat,
  toPayment,
  toggleCheckOthers,
} = useRoamingInfoDetails(props);
</script>

<template>
  <div class="roaming-info__details">
    <div class="roaming-info__details-wrapper">
      <div class="roaming-info__details-name">
        <img
          class="roaming-info__details-name__image"
          :src="country?.img_url"
          :alt="t('roaming.list.flagAlt')"
        />
        <span class="h2 color-text-primary">{{
          country?.country_name_ru
        }}</span>
      </div>
      <div class="roaming-info__details-others">
        <div class="roaming-info__details-others__text">
          <span class="caption-medium normal"
            >{{
              t("roaming.details.sameConditionsFor", {
                count: othersCountriesCountFormat,
              })
            }}
          </span>
          <span
            @click="toggleCheckOthers"
            class="caption-medium color-primary normal roaming-info__details-others__check"
          >
            {{ t("roaming.details.viewList") }}
          </span>
        </div>
        <Transition name="drop" mode="out-in">
          <RoamingCountersListWindow
            class="roaming-info__details-others__list"
            v-if="othersCountriesList && isShowOthers"
            :countersList="othersCountriesList"
          />
        </Transition>
      </div>
      <div class="roaming-info__details-conditions">
        <div class="roaming-info__details-conditions__title">
          <span class="color-text-primary bold">{{
            t("roaming.details.baseConditions")
          }}</span>
        </div>
        <div v-if="zone" class="roaming-info__details-conditions__list">
          <div
            class="roaming-info__details-conditions__resource"
            v-for="(item, index) in conditions"
            :key="index"
          >
            <div class="roaming-info__details-conditions__resource-title">
              <span class="caption-normal color-text-primary bold">{{
                item.title
              }}</span>
            </div>
            <div class="roaming-info__details-conditions__resource-options">
              <div
                class="roaming-info__details-conditions__resource-options__item"
                v-for="(optionItem, idx) in item.options"
                :key="idx"
              >
                <div
                  class="roaming-info__details-conditions__resource-options__item-text"
                >
                  <span class="caption-medium">{{ optionItem.text }}</span>
                </div>
                <div
                  class="roaming-info__details-conditions__resource-options__item-value"
                >
                  <span class="caption-medium color-text-primary thick">{{
                    formatAmount(optionItem.value)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="roaming-info__details-promo">
        <div class="roaming-info__details-promo__wrapper">
          <div class="roaming-info__details-promo__header">
            <div class="roaming-info__details-promo__header-title">
              <h4 class="color-text-primary bold h2">
                {{ t("roaming.details.promo.title") }}
              </h4>
            </div>
          </div>
          <div class="roaming-info__details-promo__content">
            <span class="color-text-secondary caption-medium">{{
              t("roaming.details.promo.description")
            }}</span>
          </div>
          <div class="roaming-info__details-promo__action">
            <div class="roaming-info__details-promo__action-price">
              <div class="roaming-info__details-promo__action-price__title">
                <span class="footnote color-text-secondary thick">{{
                  t("roaming.details.promo.priceTitle")
                }}</span>
              </div>
              <div class="roaming-info__details-promo__action-price__value">
                <span class="bold caption-large color-text-primary">600 ₽</span>
              </div>
            </div>
            <div class="roaming-info__details-promo__action-activate">
              <BaseButton
                :title="t('common.actions.connect')"
                :disabled="isLoadingActivate"
                @click="activate"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Teleport to=".root">
    <BaseModal
      v-if="isSuccessful"
      :title="t('roaming.details.modals.successTitle')"
      :main-btn-title="t('common.actions.ok')"
      @close="closeAllModal"
      @action="closeAllModal"
    />
    <BaseModal
      v-if="isUnsuccessful"
      :title="t('roaming.details.modals.insufficientFundsTitle')"
      :text="t('roaming.details.modals.insufficientFundsText')"
      :main-btn-title="t('common.buttons.topUpBalance')"
      @close="closeAllModal"
      @action="toPayment"
    />
    <BaseModal
      v-if="isUnhandledError"
      :title="t('common.errors.title')"
      :text="t('common.errors.retryRequest')"
      :main-btn-title="t('common.actions.ok')"
      @close="closeAllModal"
      @action="closeAllModal"
    />
    <BaseModal
      v-if="isIncompatible"
      :title="t('common.errors.title')"
      :text="t('roaming.details.modals.incompatibleText')"
      :main-btn-title="t('common.actions.ok')"
      @close="closeAllModal"
      @action="closeAllModal"
    />
    <BaseModal
      v-if="isAlreadyAdded"
      :title="t('common.errors.title')"
      :text="t('roaming.details.modals.alreadyAddedText')"
      :main-btn-title="t('common.actions.ok')"
      @close="closeAllModal"
      @action="closeAllModal"
    />
  </Teleport>
</template>

<style scoped lang="scss">
@import "@/assets/style/colors";

.roaming-info__details {
  border-radius: 24px;
  background-color: var(--color-bg-primary);
  @media (max-width: 768px) {
    background-color: var(--color-white);
  }
  &-wrapper {
    padding: 24px;
    @media (max-width: 768px) {
      padding: 20px;
    }
  }
  &-name {
    display: flex;
    gap: 12px;
    align-items: center;
    &__image {
      width: 24px;
    }
  }
  &-others {
    margin-top: 15px;
    position: relative;
    &__text {
      color: var(--color-text-secondary);
      display: flex;
      gap: 3px;
      flex-wrap: wrap;
      @media (max-width: 768px) {
        max-width: 200px;
      }
      span.caption-medium {
        font-size: 16px;
        @media (max-width: 1600px) {
          font-size: 16px;
        }
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
    &__check {
      cursor: pointer;
    }
    &__list {
      top: calc(100% + 12px);
      position: absolute;
    }
  }
  &-conditions {
    margin-top: 37px;
    width: 100%;
    @media (max-width: 1600px) {
      margin-top: 42px;
    }
    @media (max-width: 768px) {
      margin-top: 23px;
    }
    &__title {
      font-size: 20px;
      line-height: 130%;
      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
    &__list {
      margin-top: 23px;
      display: flex;
      flex-direction: column;
      gap: 32px;
      flex: 1;
      width: 100%;
      @media (max-width: 768px) {
        margin-top: 12px;
        gap: 16px;
      }
    }
    &__resource {
      &-title {
        margin-bottom: 8px;
        .caption-normal {
          line-height: 22px;
          @media (max-width: 1600px) {
            font-size: 18px;
            line-height: 22px;
          }
          @media (max-width: 768px) {
            font-size: 14px;
          }
        }
      }
      &-options {
        display: flex;
        gap: 8px;
        flex-direction: column;
        width: 100%;
        flex: 1;
        @media (max-width: 1600px) {
          padding-right: 24px;
        }
        @media (max-width: 1250px) {
          padding-right: 0;
        }
        &__item {
          display: flex;
          justify-content: space-between;
          gap: 17px;
          &-text {
            color: var(--color-text-secondary);
            .caption-medium {
              line-height: 130%;
              @media (max-width: 1600px) {
                font-size: 16px;
                line-height: 130%;
              }
              @media (max-width: 768px) {
                font-size: 14px;
              }
            }
          }
          &-value {
            .caption-medium {
              line-height: 130%;
              @media (max-width: 1600px) {
                font-size: 16px;
                line-height: 130%;
              }
              @media (max-width: 768px) {
                font-size: 14px;
                font-weight: 500;
              }
            }
          }
        }
      }
    }
  }
  &-promo {
    margin-top: 44px;
    width: 100%;
    border-radius: 32px;
    background: var(--color-white);
    @media (max-width: 768px) {
      background: var(--color-bg-primary);
      margin-top: 24px;
    }

    &__wrapper {
      padding: 30px 32px 32px;
      @media (max-width: 1600px) {
        padding: 30px 32px 32px;
      }
      @media (max-width: 768px) {
        padding: 20px 20px 32px 20px;
      }
    }
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      gap: 10px;
      @media (max-width: 768px) {
        margin-bottom: 8px;
        flex-direction: column;
        align-items: start;
        justify-content: start;
        gap: 12px;
      }
      &-title {
        font-size: 32px;
        line-height: 125%;
        letter-spacing: 0.015rem;
        @media (max-width: 768px) {
          font-size: 20px;
        }
      }
    }
    &__content {
      margin-bottom: 24px;
      span.caption-medium {
        line-height: 130%;
        @media (max-width: 1600px) {
          font-size: 16px;
        }
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
    &__action {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 5px;
      &-price {
        display: flex;
        flex-direction: column;
        gap: 4px;
        &__value {
          .caption-large {
            @media (max-width: 1600px) {
              font-size: 20px;
            }
          }
        }
      }
      &-activate {
        display: block;
      }
    }
  }
}
.drop {
  &-enter-from,
  &-leave-to {
    display: grid;
    opacity: 0.5;
    grid-template-rows: 0fr;
  }
  &-enter-to,
  &-leave-from {
    display: grid;
    opacity: 1;
    grid-template-rows: 1fr;
  }
  &-enter-active,
  &-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
  }
}
</style>
